import { createClient } from '@supabase/supabase-js';

// Reemplaza con tus credenciales de Supabase
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// Obtener usuario actual
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Funciones de cursos
export const coursesService = {
  // Obtener todos los cursos
  getAll: async () => {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Obtener un curso por ID
  getById: async (id) => {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  // Crear curso
  create: async (course) => {
    const { data, error } = await supabase
      .from('courses')
      .insert([course])
      .select();
    if (error) throw error;
    return data[0];
  },

  // Actualizar curso
  update: async (id, updates) => {
    const { data, error } = await supabase
      .from('courses')
      .update(updates)
      .eq('id', id)
      .select();
    if (error) throw error;
    return data[0];
  },

  // Borrar curso
  delete: async (id) => {
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// Funciones de usuarios
export const usersService = {
  // Obtener todos los usuarios
  getAll: async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Obtener perfil del usuario actual
  getProfile: async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  },

  // Actualizar perfil
  updateProfile: async (userId, updates) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select();
    if (error) throw error;
    return data[0];
  },

  // Crear un usuario como en formulario de registro (email + password + metadata)
  createWithPassword: async ({ username, fullName, email, password }) => {
    const isolated = createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: 'bulk-create-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      },
    });

    const normalizedEmail = String(email || '').trim().toLowerCase();
    const { data, error } = await isolated.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          full_name: fullName,
          username: username || null,
        },
      },
    });
    if (error) throw error;

    const createdUser = data?.user || null;
    if (createdUser) {
      try {
        await isolated.from('user_profiles').upsert([
          {
            id: createdUser.id,
            email: normalizedEmail,
            full_name: fullName,
            role: 'student',
          },
        ]);
      } catch {
        // Si existe trigger en DB o la policy bloquea upsert, no interrumpimos la creación auth.
      }
    }

    return {
      user: createdUser,
      needsEmailConfirmation: !data?.session,
    };
  },
};

// Grupos persistidos en Supabase
export const groupsService = {
  getAll: async () => {
    const { data: groups, error } = await supabase
      .from('groups')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;

    if (!groups || groups.length === 0) return [];
    const groupIds = groups.map((g) => g.id);

    const [{ data: members, error: membersError }, { data: courses, error: coursesError }] = await Promise.all([
      supabase.from('group_members').select('group_id,user_id').in('group_id', groupIds),
      supabase.from('group_courses').select('group_id,course_id').in('group_id', groupIds),
    ]);
    if (membersError) throw membersError;
    if (coursesError) throw coursesError;

    return groups.map((g) => ({
      ...g,
      studentIds: (members || []).filter((m) => m.group_id === g.id).map((m) => m.user_id),
      courseIds: (courses || []).filter((c) => c.group_id === g.id).map((c) => c.course_id),
    }));
  },

  save: async ({ id, name, description, created_by }) => {
    if (id) {
      const { data, error } = await supabase
        .from('groups')
        .update({ name, description })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    }

    const { data, error } = await supabase
      .from('groups')
      .insert([{ name, description, created_by }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  setMembers: async (groupId, studentIds) => {
    const { error: deleteError } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId);
    if (deleteError) throw deleteError;

    const payload = (studentIds || []).map((userId) => ({ group_id: groupId, user_id: userId }));
    if (payload.length === 0) return;

    const { error: insertError } = await supabase
      .from('group_members')
      .insert(payload);
    if (insertError) throw insertError;
  },

  setCourses: async (groupId, courseIds) => {
    const { error: deleteError } = await supabase
      .from('group_courses')
      .delete()
      .eq('group_id', groupId);
    if (deleteError) throw deleteError;

    const payload = (courseIds || []).map((courseId) => ({ group_id: groupId, course_id: courseId }));
    if (payload.length === 0) return;

    const { error: insertError } = await supabase
      .from('group_courses')
      .insert(payload);
    if (insertError) throw insertError;
  },

  delete: async (groupId) => {
    const { error } = await supabase
      .from('groups')
      .delete()
      .eq('id', groupId);
    if (error) throw error;
  },
};

// Evaluaciones por curso (1 evaluacion por curso)
export const evaluationsService = {
  getByCourse: async (courseId) => {
    const { data: evaluation, error } = await supabase
      .from('course_evaluations')
      .select('*')
      .eq('course_id', courseId)
      .maybeSingle();
    if (error) throw error;
    if (!evaluation) return null;

    const { data: questions, error: questionsError } = await supabase
      .from('evaluation_questions')
      .select('*')
      .eq('evaluation_id', evaluation.id)
      .order('sort_order', { ascending: true });
    if (questionsError) throw questionsError;

    const questionIds = (questions || []).map((q) => q.id);
    let options = [];
    if (questionIds.length > 0) {
      const { data: optionsData, error: optionsError } = await supabase
        .from('evaluation_options')
        .select('*')
        .in('question_id', questionIds)
        .order('sort_order', { ascending: true });
      if (optionsError) throw optionsError;
      options = optionsData || [];
    }

    return {
      ...evaluation,
      questions: (questions || []).map((question) => ({
        ...question,
        options: options.filter((option) => option.question_id === question.id),
      })),
    };
  },

  saveByCourse: async ({ courseId, title, description, passingScore, questions, userId }) => {
    let evaluationId = null;
    const { data: existing } = await supabase
      .from('course_evaluations')
      .select('id')
      .eq('course_id', courseId)
      .maybeSingle();

    if (existing?.id) {
      evaluationId = existing.id;
      const { error } = await supabase
        .from('course_evaluations')
        .update({
          title,
          description,
          passing_score: passingScore,
          updated_at: new Date().toISOString(),
        })
        .eq('id', evaluationId);
      if (error) throw error;

      const { error: deleteQuestionsError } = await supabase
        .from('evaluation_questions')
        .delete()
        .eq('evaluation_id', evaluationId);
      if (deleteQuestionsError) throw deleteQuestionsError;
    } else {
      const { data, error } = await supabase
        .from('course_evaluations')
        .insert([{
          course_id: courseId,
          title,
          description,
          passing_score: passingScore,
          created_by: userId,
        }])
        .select()
        .single();
      if (error) throw error;
      evaluationId = data.id;
    }

    for (let index = 0; index < (questions || []).length; index += 1) {
      const question = questions[index];
      const { data: createdQuestion, error: questionError } = await supabase
        .from('evaluation_questions')
        .insert([{
          evaluation_id: evaluationId,
          prompt: question.prompt,
          sort_order: index + 1,
        }])
        .select()
        .single();
      if (questionError) throw questionError;

      const optionsPayload = (question.options || []).map((option, optionIndex) => ({
        question_id: createdQuestion.id,
        option_text: option.option_text,
        is_correct: !!option.is_correct,
        sort_order: optionIndex + 1,
      }));

      if (optionsPayload.length > 0) {
        const { error: optionsError } = await supabase
          .from('evaluation_options')
          .insert(optionsPayload);
        if (optionsError) throw optionsError;
      }
    }

    return evaluationsService.getByCourse(courseId);
  },

  deleteByCourse: async (courseId) => {
    const { error } = await supabase
      .from('course_evaluations')
      .delete()
      .eq('course_id', courseId);
    if (error) throw error;
  },

  getLatestAttempt: async (evaluationId) => {
    const { data, error } = await supabase
      .from('evaluation_attempts')
      .select('*')
      .eq('evaluation_id', evaluationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  },

  createAttempt: async ({ evaluationId, courseId, score, passed, answers }) => {
    const { data: authData } = await supabase.auth.getUser();
    const currentUser = authData?.user;
    const { data, error } = await supabase
      .from('evaluation_attempts')
      .insert([{
        evaluation_id: evaluationId,
        course_id: courseId,
        user_id: currentUser?.id,
        score,
        passed,
        answers,
      }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// Funciones de lecciones
export const lessonsService = {
  // Obtener lecciones de un curso
  getByCourse: async (courseId) => {
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('course_id', courseId)
      .order('orden', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Crear lección
  create: async (lesson) => {
    const { data, error } = await supabase
      .from('lessons')
      .insert([lesson])
      .select();
    if (error) throw error;
    return data[0];
  },

  // Actualizar lección
  update: async (id, updates) => {
    console.log('[Supabase] UPDATE lessons:', { id, updates });
    const { data, error } = await supabase
      .from('lessons')
      .update(updates)
      .eq('id', id)
      .select();
    if (error) {
      console.error('[Supabase] Error UPDATE:', error);
      throw error;
    }
    console.log('[Supabase] UPDATE resultado:', data);
    // Si data es un array vacío (problema RLS), retorna null pero no falla
    if (data && data.length > 0) {
      return data[0];
    }
    console.log('[Supabase] ⚠️ SELECT después UPDATE retornó vacío (posible RLS), pero UPDATE fue exitoso');
    return null;
  },

  // Borrar lección
  delete: async (id) => {
    const { error } = await supabase
      .from('lessons')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// Funciones de inscripciones
export const enrollmentsService = {
  // Cursos a los que tiene acceso el usuario actual
  getMyCourses: async () => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('course_id, enrolled_at, courses(*)')
      .order('enrolled_at', { ascending: false });
    if (error) throw error;
    return data.map((e) => e.courses);
  },

  // Obtener cursos de un usuario específico (por su ID)
  getUserCourses: async (userId) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('course_id, enrolled_at, courses(*)')
      .eq('user_id', userId)
      .order('enrolled_at', { ascending: false });
    if (error) throw error;
    return data.map((e) => e.courses);
  },

  // Verificar si el usuario tiene acceso a un curso
  hasAccess: async (courseId) => {
    const { data } = await supabase
      .from('enrollments')
      .select('id')
      .eq('course_id', courseId)
      .maybeSingle();
    return !!data;
  },

  // (Admin) Obtener todos los inscritos de un curso
  getByCourse: async (courseId) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('user_id, enrolled_at, user_profiles(id, email, full_name, role)')
      .eq('course_id', courseId);
    if (error) throw error;
    return data;
  },

  // (Admin) Inscribir usuario en un curso
  enroll: async (userId, courseId) => {
    const { data, error } = await supabase
      .from('enrollments')
      .insert([{ user_id: userId, course_id: courseId }])
      .select();
    if (error) throw error;
    return data[0];
  },

  // (Admin) Quitar acceso a un curso
  unenroll: async (userId, courseId) => {
    const { error } = await supabase
      .from('enrollments')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);
    if (error) throw error;
  },

  // (Optimizado) Obtener enrollments sin duplicados de un usuario
  getUserEnrollmentsNoDup: async (userId) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('course_id, courses(*)')
      .eq('user_id', userId);
    if (error) throw error;
    // Deduplicar por course_id en caso de duplicados en BD
    const seen = new Set();
    return data
      .filter((e) => {
        if (seen.has(e.course_id)) return false;
        seen.add(e.course_id);
        return true;
      })
      .map((e) => e.courses);
  },
};

// Seguimiento de progreso de lecciones
export const lessonProgressService = {
  // Recibe los IDs de lecciones del curso y devuelve cuáles están completadas
  getCompleted: async (lessonIds) => {
    if (!lessonIds || lessonIds.length === 0) return [];
    const { data, error } = await supabase
      .from('lesson_progress')
      .select('lesson_id')
      .in('lesson_id', lessonIds);
    if (error) throw error;
    return data.map((r) => r.lesson_id);
  },

  // Marcar lección como completada
  markComplete: async (lessonId) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('lesson_progress')
      .upsert([{ user_id: user.id, lesson_id: lessonId }], { onConflict: 'user_id,lesson_id' });
    if (error) throw error;
  },

  // Desmarcar lección completada
  markIncomplete: async (lessonId) => {
    const { error } = await supabase
      .from('lesson_progress')
      .delete()
      .eq('lesson_id', lessonId);
    if (error) throw error;
  },
};
